import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import FeaturedPost from './components/FeaturedPost';
import BlogCard from './components/BlogCard';
import BlogFilters from './components/BlogFilters';
import NewsletterSection from './components/NewsletterSection';
import PopularTags from './components/PopularTags';
import RelatedPosts from './components/RelatedPosts';
import ResourcesSection from './components/ResourcesSection';
import {
  BlogPost,
  BlogCategory,
  BlogFilters as BlogFiltersType,
  Author,
  RelatedPost
} from './types';
import { blogPosts } from './posts';

// 🎨 Same categories your design expects
const categories: BlogCategory[] = [
  {
    id: 'tutorials',
    name: 'Tutorials',
    slug: 'tutorials',
    description: 'Step-by-step automation guides',
    icon: 'BookOpen',
    color: '#2563EB'
  },
  {
    id: 'case-studies',
    name: 'Case Studies',
    slug: 'case-studies',
    description: 'Real-world automation success stories',
    icon: 'FileText',
    color: '#10B981'
  },
  {
    id: 'strategy',
    name: 'Strategy',
    slug: 'strategy',
    description: 'Automation strategy & best practices',
    icon: 'Target',
    color: '#F59E0B'
  },
  {
    id: 'tool-guides',
    name: 'Tool Guides',
    slug: 'tool-guides',
    description: 'Platform-specific tutorials',
    icon: 'Wrench',
    color: '#EF4444'
  }
];

// 👤 Default author – overridden by DB data when available
const defaultAuthor: Author = {
  id: 'prince',
  name: 'Prince Kumar',
  role: 'Founder, Make Mind Automations',
  avatar: 'https://images.unsplash.com/photo-1544723795-3fb6469f5b39',
  avatarAlt: 'Portrait of automation consultant',
  bio: 'Automation strategist helping businesses save time with n8n, Make.com and AI workflows.',
  socialLinks: {
    twitter: 'https://twitter.com',
    linkedin: 'https://linkedin.com'
  }
};

const Blog = () => {
  const [filters, setFilters] = useState<BlogFiltersType>({
    category: 'all',
    tag: '',
    search: '',
    sortBy: 'latest'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;

  // Static blog posts: no Supabase call. See src/pages/blog/posts.ts.
  // The admin blog editor no longer feeds the live blog.
  const [allPosts] = useState<BlogPost[]>(blogPosts);
  const [loading] = useState(false);
  const [error] = useState<string | null>(null);

  // 🧠 Filters
  const handleFilterChange = (updated: BlogFiltersType) => {
    setFilters(updated);
    setCurrentPage(1);
  };

  const filterPosts = (posts: BlogPost[]): BlogPost[] => {
    let filtered = [...posts];

    if (filters.category !== 'all') {
      filtered = filtered.filter(
        (post) => post.category.slug === filters.category
      );
    }

    if (filters.tag) {
      filtered = filtered.filter((post) => post.tags.includes(filters.tag));
    }

    if (filters.search) {
      const q = filters.search.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(q) ||
          post.excerpt.toLowerCase().includes(q)
      );
    }

    if (filters.sortBy === 'latest') {
      filtered.sort(
        (a, b) => b.publishedAt.getTime() - a.publishedAt.getTime()
      );
    } else if (filters.sortBy === 'popular') {
      filtered.sort((a, b) => (b.views ?? 0) - (a.views ?? 0));
    } else if (filters.sortBy === 'trending') {
      filtered.sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0));
    }

    return filtered;
  };

  const filteredPosts = filterPosts(allPosts);

  const featuredPost =
    filteredPosts.find((post: any) => post.isFeatured) || filteredPosts[0];

  const regularPosts = featuredPost
    ? filteredPosts.filter((post) => post.id !== featuredPost.id)
    : filteredPosts;

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = regularPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages =
    regularPosts.length > 0
      ? Math.ceil(regularPosts.length / postsPerPage)
      : 1;

  // 🔥 Popular tags from real posts
  const tagCounts: Record<string, number> = {};
  allPosts.forEach((post) => {
    post.tags.forEach((tag) => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });
  });

  const popularTags = Object.entries(tagCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // 🎯 Simple related posts: first 3 regular posts
  const relatedPosts: RelatedPost[] = regularPosts.slice(0, 3).map((post) => ({
    id: post.id,
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt,
    category: post.category.name,
    readTime: post.readTime,
    featuredImage: post.featuredImage,
    featuredImageAlt: post.featuredImageAlt,
    publishedAt: post.publishedAt
  }));

  // Keep your scroll-to-top behavior
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Automation Blog & Resources | Make Mind Automations</title>
        <meta
          name="description"
          content="Learn how to use n8n, Make.com, AI chatbots and automation to grow your business. Tutorials, case studies, and playbooks from Make Mind Automations."
        />
        <link
  rel="canonical"
  href="https://www.makemindautomations.tech/blog"
/>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-16 space-y-12">
          {/* Hero section */}
          <section className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-3">
                <Icon name="Sparkles" className="w-3.5 h-3.5" />
                <span>Automation Insights & Tutorials</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-3">
                Learn Automation That Actually Grows Your Business
              </h1>
              <p className="text-text-secondary max-w-2xl mx-auto md:mx-0">
                Deep-dive tutorials, real client case studies, and practical
                playbooks for n8n, Make.com, AI chatbots, and custom workflows.
              </p>
            </div>

            <div className="flex flex-col items-stretch sm:items-center md:items-end gap-3">
              <Button asChild>
                <a href="/contact" className="w-full sm:w-auto text-center">
                  <Icon name="Sparkles" className="w-4 h-4 mr-2" />
                  Get a free automation audit
                </a>
              </Button>
              <span className="text-xs text-text-secondary text-center md:text-right">
                No fluff. Just battle-tested automation strategies.
              </span>
            </div>
          </section>

          {/* Filters */}
          <section>
            <BlogFilters
              categories={[
                {
                  id: 'all',
                  name: 'All',
                  slug: 'all',
                  description: 'All articles',
                  icon: 'Globe',
                  color: '#6B7280'
                },
                ...categories
              ]}
              filters={filters}
              onFilterChange={handleFilterChange}
              totalResults={filteredPosts.length}
            />
          </section>

          {/* Content */}
          {loading ? (
            <p className="text-center text-text-secondary mt-10">
              Loading blog posts...
            </p>
          ) : error ? (
            <p className="text-center text-red-500 mt-10">
              Failed to load posts: {error}
            </p>
          ) : filteredPosts.length === 0 ? (
            <p className="text-center text-text-secondary mt-10">
              No posts match your filters.
            </p>
          ) : (
            <>
              {featuredPost && (
                <section>
                  <FeaturedPost post={featuredPost} />
                </section>
              )}

              {/* Main + sidebar layout */}
              <section className="grid gap-8 lg:grid-cols-3">
                {/* Main list */}
                <div className="space-y-6 lg:col-span-2">
                  {currentPosts.map((post) => (
                    <BlogCard key={post.id} post={post} />
                  ))}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between mt-6">
                      <span className="text-sm text-text-secondary">
                        Page {currentPage} of {totalPages}
                      </span>
                      <div className="flex w-full sm:w-auto justify-between sm:justify-end gap-2">
                        <Button
                          variant="outline"
                          disabled={currentPage === 1}
                          onClick={() =>
                            setCurrentPage((p) => Math.max(1, p - 1))
                          }
                          className="flex-1 sm:flex-none"
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          disabled={currentPage === totalPages}
                          onClick={() =>
                            setCurrentPage((p) => Math.min(totalPages, p + 1))
                          }
                          className="flex-1 sm:flex-none"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <aside className="space-y-6 lg:col-span-1">
                  <PopularTags
                    tags={popularTags}
                    onTagClick={(tag) =>
                      handleFilterChange({
                        ...filters,
                        tag,
                        category: 'all'
                      })
                    }
                  />
                  <RelatedPosts posts={relatedPosts} />
                  <ResourcesSection />
                </aside>
              </section>

              <section className="mt-16">
                <NewsletterSection />
              </section>
            </>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Blog;
